import moment from "moment-timezone";

import { useContext, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LanguageIcon from "@mui/icons-material/Language";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PlaceIcon from "@mui/icons-material/Place";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";

import { useAuth } from "../hooks/useAuth";
import useEvent from "../hooks/useEvent";
import usePageTitle from "../hooks/usePageTitle";

import { ToastContext } from "../Components/ToastProvider";
import LoadingButton from "../Components/LoadingButton";
import LoginCTA from "../Components/LoginCTA";
import Modal from "../Components/Modal";
import CompleteProfileModal from "./CompleteProfileModal";

import "../App.css";

// Generates a consistent-ish avatar icon
const avatarEmojis = ["🍜", "🥟", "🍱", "🍕"];

function UserAvatar({ username, index = 0 }) {
  return <div className="attendee-avatar">{avatarEmojis[index % 4]}</div>;
}

function Event() {
  const { authed, profile } = useAuth();
  const { id } = useParams();

  const { createToast, dismissToast } = useContext(ToastContext);

  const errorToastRef = useRef(null);

  const {
    event,
    error,
    joinEvent,
    hasJoinedEvent,
    unjoinEvent,
    isOwner,
    cancelEvent,
    createMessage,
    loading,
    newMessage,
    setNewMessage,
  } = useEvent(id);

  if (newMessage) {
    createToast("There's a new message on this event", "notice");
    setNewMessage(false);
  }

  const [disabled, setDisabled] = useState(false);
  const [isModalOpen, toggleIsModalOpen] = useState(false);
  const [isUnjoinModalOpen, toggleIsUnjoinModalOpen] = useState(false);
  const [isCompleteProfileModalOpen, toggleIsCompleteProfileModalOpen] =
    useState(false);
  const [isCTAModalOpen, toggleCTAModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const maxChars = 300;
  const inThreeHours = moment().add(3, "h");
  const navigate = useNavigate();

  usePageTitle(`${event?.title} | Fudopia | ${event?.city?.title}`);

  function handleJoinEvent({
    commandFromCompleteProfileModal,
    updatedProfile,
  } = {}) {
    const food = updatedProfile?.favoriteFood || profile.favorite_food;
    const g = updatedProfile?.gender || profile.gender;
    const spice = updatedProfile?.spiceLevel || profile.spice_level;
    const ask = updatedProfile?.askMeAbout || profile.ask_me_about;
    const ageRange = updatedProfile?.age || profile.age_range;
    const cuisineList = updatedProfile?.cuisineIds || profile.cuisines;

    const showProfileFields =
      !food || !g || !spice || !ask || !ageRange || !cuisineList?.length;

    if (showProfileFields) {
      if (commandFromCompleteProfileModal) {
        setShowErrorMessage(true);
      } else {
        toggleIsCompleteProfileModalOpen(true);
        setShowErrorMessage(false);
      }
      return false;
    }

    if (errorToastRef.current) {
      dismissToast(errorToastRef.current);
      errorToastRef.current = null;
    }

    setDisabled(true);
    joinEvent(id).then(() => {
      createToast("You successfully joined this event 😊", "success", true);
      setDisabled(false);
    });
  }

  function handleUnjoinEvent() {
    toggleIsUnjoinModalOpen(false);
    setDisabled(true);
    unjoinEvent(id).then(async () => {
      createToast("You successfully left this event", "success");
      setDisabled(false);
    });
  }

  function handleCancelEvent() {
    toggleIsModalOpen(!isModalOpen);
    setDisabled(true);
    cancelEvent(id).then(async (success) => {
      if (success) {
        createToast("You canceled the event", "success", true);
        navigate(`../../events/`, { replace: true });
      } else {
        createToast("Please try again...", "warning");
      }
      setDisabled(false);
    });
  }

  const handleCreateMessage = (e) => {
    e.preventDefault();
    setDisabled(true);
    const data = { event_id: id, body: message };
    createMessage(data).then(async (success) => {
      if (success) {
        setMessage("");
      } else {
        createToast("Please try again...", "warning");
      }
      setDisabled(false);
    });
  };

  // Sidebar card state
  const isEventPast = moment(event?.date) <= moment().tz(event?.timezone);
  const isTooLateToJoin = !moment(event?.date).isAfter(inThreeHours);
  const isFull = authed
    ? event?.clients?.length === 4
    : event?.client_count === 4;
  const spotsLeft =
    4 - (authed ? event?.clients?.length || 0 : event?.client_count || 0);
  const showJoinCard =
    !hasJoinedEvent && !isOwner && !isFull && !isTooLateToJoin && !isEventPast;

  return (
    <>
      {/* MODALS */}
      <Modal
        title="You're about to join. Let's get you in!"
        isOpen={isCTAModalOpen}
        handleDismiss={() => toggleCTAModalOpen(!isCTAModalOpen)}
        description="Looks like you're ready to grab a seat.
Just log in or sign up to save your spot.
✨ First 200 members get a free account for a whole year."
      >
        <div className="btn-inline">
          <Link to="/register">
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCancelEvent}
            >
              Create Free Account
            </Button>
          </Link>
          <Link to="/login">
            <Button
              color="primary"
              variant="contained"
              onClick={() => toggleIsModalOpen(!isModalOpen)}
            >
              Log In
            </Button>
          </Link>
        </div>
      </Modal>

      <Modal
        title="Are you sure about canceling this event?"
        isOpen={isModalOpen}
        handleDismiss={() => toggleIsModalOpen(!isModalOpen)}
      >
        {!moment(event?.date).isAfter(moment().add(1, "h")) && (
          <p className="centered">
            The meetup starts soon, and attendees may already be on the way.{" "}
            <br />
            If you can, please leave a short update in the event chat <br />
            so everyone knows what's happening.
          </p>
        )}
        <div className="btn-inline">
          <Button
            color="primary"
            variant="outlined"
            onClick={handleCancelEvent}
          >
            Yes
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => toggleIsModalOpen(!isModalOpen)}
          >
            No
          </Button>
        </div>
      </Modal>

      <Modal
        title="Leaving already?"
        isOpen={isUnjoinModalOpen}
        handleDismiss={() => toggleIsUnjoinModalOpen(!isUnjoinModalOpen)}
      >
        {!moment(event?.date).isAfter(moment().add(1, "h")) ? (
          <p className="centered">
            The meetup starts soon, and others may already be on the way. <br />
            If possible, send a short update in the event chat <br />
            (for example, "Running a bit late" or "I can't make it today"){" "}
            <br />
            so everyone knows what's happening.
          </p>
        ) : (
          <>
            <p style={{ textAlign: "center" }}>
              Are you sure you want to leave this event?
            </p>
            <p style={{ marginBottom: 40, textAlign: "center" }}>
              We'll miss you at the table, but totally understand if plans
              change.
            </p>
          </>
        )}
        <div className="btn-inline">
          <Button
            color="secondary"
            variant="contained"
            onClick={() => toggleIsUnjoinModalOpen(!isUnjoinModalOpen)}
          >
            Keep My Spot
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleUnjoinEvent}
          >
            Leave event
          </Button>
        </div>
      </Modal>

      <CompleteProfileModal
        isOpen={isCompleteProfileModalOpen}
        toggleOpen={toggleIsCompleteProfileModalOpen}
        onSave={handleJoinEvent}
        showErrorMessage={showErrorMessage}
      />

      {error && <p className="error-message">Something went wrong...</p>}
      {loading && (
        <div className="block-wrapper">
          <Skeleton animation="wave" height={600} />
        </div>
      )}

      {!loading && event && (
        <>
          <div className="block event">
            {/* HERO */}
            <div className="event-hero">
              <div className="event-tag">
                🍽️ {event.location?.city?.title} · {event.languages}
              </div>
              <h1>{event.title}</h1>
              <div className="event-hero-meta">
                <span>
                  🗓{" "}
                  {moment(event.date)
                    .tz(event.timezone)
                    .format(
                      authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do",
                    )}
                </span>
                <span>📍 {event.location?.title}</span>
                {event.active &&
                  !isFull &&
                  !isTooLateToJoin &&
                  !hasJoinedEvent && (
                    <span>
                      ✨ {spotsLeft} {spotsLeft === 1 ? "spot" : "spots"} left
                    </span>
                  )}
              </div>
            </div>

            {/* TWO COLUMN LAYOUT */}
            <div className="event-layout">
              {/* LEFT / MAIN COLUMN */}
              <div className="event-main">
                {/* DISH */}
                <div className="dish-card">
                  <img
                    src={event.dish?.image_url}
                    alt={event.dish?.title}
                    className="dish-card-img"
                  />
                  <div className="dish-card-text">
                    <p className="dish-we-trying">We're trying</p>
                    <h3>
                      <Link to={`/dishes/${event.dish?.id}`}>
                        {event.dish?.title}
                      </Link>
                    </h3>
                    <p>
                      This dish sets the tone for the group. Share it, skip it,
                      or just use it to spark conversation.
                    </p>
                  </div>
                </div>

                {/* ABOUT */}
                <div className="card">
                  <p className="card-label">About this meetup</p>
                  <p className="event-description">{event.body}</p>
                </div>

                {/* WHO'S COMING */}
                {authed ? (
                  <div className="card">
                    <p className="card-label">Who's Coming</p>
                    {event.clients.length > 0 ? (
                      <div className="attendees">
                        {event.clients.map((client, i) => (
                          <Link
                            to={`/users/${client.username}`}
                            key={i}
                            className="attendee-link"
                          >
                            <div className="attendee-card">
                              <div className="attendee-card-top">
                                <UserAvatar
                                  username={client.username}
                                  index={i}
                                />
                                <div className="attendee-info">
                                  <span className="attendee-name">
                                    {client.username}
                                  </span>
                                  <div className="attendee-meta">
                                    {client.gender && (
                                      <span>{client.gender}</span>
                                    )}
                                    {client.gender && client.age_range && (
                                      <span>·</span>
                                    )}
                                    {client.age_range && (
                                      <span>{client.age_range}</span>
                                    )}
                                    {client.spice_level && <span>·</span>}
                                    {client.spice_level && (
                                      <span>{client.spice_level}</span>
                                    )}
                                  </div>
                                </div>

                                <span
                                  className={`attendee-events${client.event_count === 1 ? " attendee-events--first" : ""}`}
                                >
                                  {client.event_count === 1
                                    ? "✨ First meetup!"
                                    : `${client.event_count} meetups`}
                                </span>
                              </div>

                              {client.favorite_food && (
                                <div className="attendee-dish">
                                  🍽️ {client.favorite_food}
                                </div>
                              )}

                              {client.bio && (
                                <div className="attendee-bio">
                                  "{client.bio}"
                                </div>
                              )}

                              {client.ask_me_about && (
                                <>
                                  <p className="attendee-ask-label">
                                    Ask me about
                                  </p>
                                  <p className="attendee-ask">
                                    {client.ask_me_about}
                                  </p>
                                </>
                              )}

                              {client.cuisines?.length > 0 && (
                                <>
                                  <p className="attendee-cuisines-label">
                                    Favourite cuisines
                                  </p>
                                  <div className="chips">
                                    {client.cuisines.slice(0, 3).map((c) => (
                                      <span
                                        key={c.id}
                                        className="chip chip--display"
                                      >
                                        {c.title}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>This event's waiting for its first foodie!</p>
                        <p>Hit "Join Event" and lead the way 🎉</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card">
                    <p className="card-label">Who's Coming</p>
                    {event.client_count > 0 ? (
                      <p>
                        {event.client_count === 1
                          ? "1 foodie already signed up!"
                          : `${event.client_count} people joined already`}
                      </p>
                    ) : (
                      <p>This event's waiting for its first foodie!</p>
                    )}
                    <p>
                      <Link to="/register/">Create a free account</Link> or{" "}
                      <Link to="/login">log in</Link> to see who's joining.
                    </p>
                  </div>
                )}

                {/* DISCUSSIONS */}
                {hasJoinedEvent ? (
                  <div className="card discussion-card">
                    <div className="discussion-card-header">
                      <p className="card-label">Discussions</p>
                      {event.discussions.length > 0 && (
                        <span className="discussion-count">
                          {event.discussions.length}{" "}
                          {event.discussions.length === 1
                            ? "message"
                            : "messages"}
                        </span>
                      )}
                    </div>
                    {event.discussions.length > 0 ? (
                      <ul className="discussion-list">
                        {event.discussions.map((discussion) => (
                          <li key={discussion.id} className="discussion-item">
                            <UserAvatar
                              username={discussion.author.username}
                              index={event.clients.findIndex(
                                (c) =>
                                  c.username === discussion.author.username,
                              )}
                            />
                            <div className="discussion-content">
                              <div className="discussion-header">
                                <span className="discussion-author">
                                  {discussion.author.username}
                                </span>
                                <span className="discussion-time">
                                  {moment(discussion.created_at)
                                    .tz(event.timezone)
                                    .format("MMM Do · h:mm a")}
                                </span>
                              </div>
                              <p className="discussion-body">
                                {discussion.body}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="discussion-empty">
                        No messages yet. Say hi! 👋
                      </p>
                    )}
                    <form
                      className="message-input"
                      onSubmit={handleCreateMessage}
                    >
                      <textarea
                        rows="3"
                        placeholder="Your message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={maxChars}
                        required
                      />
                      <div className="message-footer">
                        <span className="char-count">
                          {message.length} / {maxChars}
                        </span>
                        <LoadingButton
                          color="primary"
                          type="submit"
                          variant="contained"
                          disabled={disabled}
                        >
                          Send
                        </LoadingButton>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="card discussion-card discussion-card--locked">
                    <p className="card-label">Discussions</p>
                    <p className="discussion-locked-body">
                      {authed
                        ? "Join this event to chat with other attendees."
                        : "Log in and join this event to chat with other attendees."}
                    </p>
                  </div>
                )}
              </div>

              {/* RIGHT / SIDEBAR COLUMN */}
              <div className="event-sidebar">
                {/* ACTION CARD */}
                {event.active ? (
                  <div
                    className={`sidebar-action-card${showJoinCard ? " sidebar-action-card--join" : ""}`}
                  >
                    {/* NOT LOGGED IN */}
                    {!authed && (
                      <>
                        {!isFull && !isTooLateToJoin ? (
                          <>
                            <div className="spots-count">{spotsLeft}</div>
                            <div className="spots-label">
                              {spotsLeft === 1 ? "spot" : "spots"} left
                            </div>
                            <div className="spots-bar-track">
                              <div
                                className="spots-bar-fill"
                                style={{
                                  width: `${((4 - spotsLeft) / 4) * 100}%`,
                                }}
                              />
                            </div>
                            <button
                              className="btn-join"
                              onClick={() => toggleCTAModalOpen(true)}
                            >
                              Join Event ✨
                            </button>
                          </>
                        ) : isFull ? (
                          <p className="sidebar-status">This event is full</p>
                        ) : (
                          <p className="sidebar-status">
                            Too late to join this one
                          </p>
                        )}
                      </>
                    )}

                    {/* LOGGED IN — OWNER */}
                    {authed && isOwner && (
                      <>
                        {isEventPast ? (
                          <p className="sidebar-status">
                            This event has passed
                          </p>
                        ) : (
                          <>
                            <div className="sidebar-badge sidebar-badge--owner">
                              🍴 You created this
                            </div>
                            <p className="sidebar-hint">
                              You're hosting this meetup. Attendees can see your
                              event and join the chat.
                            </p>
                            <button
                              className="btn-destructive"
                              onClick={() => toggleIsModalOpen(!isModalOpen)}
                              disabled={disabled}
                            >
                              Cancel Event
                            </button>
                          </>
                        )}
                      </>
                    )}

                    {/* LOGGED IN — JOINED (not owner) */}
                    {authed && !isOwner && hasJoinedEvent && (
                      <>
                        {isEventPast ? (
                          <p className="sidebar-status">
                            This event has passed
                          </p>
                        ) : (
                          <>
                            <div className="sidebar-badge sidebar-badge--joined">
                              ✓ You're in
                            </div>
                            <p className="sidebar-hint">
                              See you at {event.location?.title}. <br />
                              Say hi in the chat if you have any questions.
                            </p>
                            <button
                              className="btn-destructive"
                              onClick={() =>
                                toggleIsUnjoinModalOpen(!isUnjoinModalOpen)
                              }
                              disabled={disabled}
                            >
                              Leave Event
                            </button>
                          </>
                        )}
                      </>
                    )}

                    {/* LOGGED IN — NOT JOINED */}
                    {authed && !isOwner && !hasJoinedEvent && (
                      <>
                        {isFull ? (
                          <>
                            <p className="sidebar-status">This event is full</p>
                            <Link to="/events/">
                              <button className="btn-join">
                                See other events
                              </button>
                            </Link>
                          </>
                        ) : isTooLateToJoin ? (
                          <>
                            <p className="sidebar-status">
                              Too late to join this one
                            </p>
                            <Link to="/events/">
                              <button className="btn-join">
                                See other events
                              </button>
                            </Link>
                          </>
                        ) : isEventPast ? (
                          <p className="sidebar-status">
                            This event has passed
                          </p>
                        ) : (
                          <>
                            <div className="spots-count">{spotsLeft}</div>
                            <div className="spots-label">
                              {spotsLeft === 1 ? "spot" : "spots"} left
                            </div>
                            <div className="spots-bar-track">
                              <div
                                className="spots-bar-fill"
                                style={{
                                  width: `${((4 - spotsLeft) / 4) * 100}%`,
                                }}
                              />
                            </div>
                            <button
                              className="btn-join"
                              onClick={handleJoinEvent}
                              disabled={disabled}
                            >
                              Join Event ✨
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="sidebar-action-card">
                    <p className="sidebar-status">
                      This event is canceled or has already passed
                    </p>
                    <Link to="/events/">
                      <button className="btn-join">See other events</button>
                    </Link>
                  </div>
                )}

                {/* EVENT DETAILS */}
                <div className="card">
                  <p className="card-label">Event Details</p>
                  <div className="info-list">
                    <div className="info-row">
                      <AccessTimeIcon fontSize="small" className="info-icon" />
                      <div className="info-content">
                        <div className="info-label">Date & Time</div>
                        <div className="info-value">
                          {moment(event.date)
                            .tz(event.timezone)
                            .format(
                              authed
                                ? "dddd, MMMM Do · h:mm a"
                                : "dddd, MMMM Do",
                            )}
                          {!authed && (
                            <p className="info-hint">
                              <Link to="/login">Log in</Link> to see the time 🕦
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <LanguageIcon fontSize="small" className="info-icon" />
                      <div className="info-content">
                        <div className="info-label">Language</div>
                        <div className="info-value capitalize">
                          {event.languages}
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <LocationCityIcon
                        fontSize="small"
                        className="info-icon"
                      />
                      <div className="info-content">
                        <div className="info-label">City</div>
                        <div className="info-value">
                          {event.location?.city?.title}
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <RestaurantIcon fontSize="small" className="info-icon" />
                      <div className="info-content">
                        <div className="info-label">Where You'll Meet</div>
                        <div className="info-value">
                          <Link to={`/restaurants/${event.location?.id}`}>
                            {event.location?.title}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="info-row">
                      <PlaceIcon fontSize="small" className="info-icon" />
                      <div className="info-content">
                        <div className="info-label">Address</div>
                        <div className="info-value">
                          <Link
                            to={event.location?.google_maps}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {event.location?.address}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MOOD TAGS */}
                {event.tags?.length > 0 && (
                  <div className="card" style={{ position: "relative" }}>
                    <div className="corner-sparks">
                      <div className="spark sm blue"></div>
                      <div className="spark"></div>
                      <div className="spark sm red"></div>
                    </div>
                    <div className="mood-header">
                      <div className="spark sm"></div>
                      <span className="card-label">Meetup Mood</span>
                      <div className="spark sm blue"></div>
                    </div>
                    <div className="chips">
                      {event.tags.map((tag) => (
                        <span key={tag.id} className="chip">
                          {tag.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* BACK LINK */}
            <Link to="/events" className="back-link">
              ← Back to the Events
            </Link>
          </div>

          {!authed && <LoginCTA />}
        </>
      )}
    </>
  );
}

export default Event;
